// Platform detection + keyboard combo helpers for the shortcuts system.
// "mod" is the declarative modifier used everywhere else in the app:
// it resolves to Cmd on Mac and Ctrl on Windows/Linux, so a combo only
// needs to be declared once (see Hooks/useShortcut).

export const isMac = () => (
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '')
);

// Parses a declarative combo string (e.g. "mod+s", "mod+ArrowRight", "Escape")
// into its parts: whether it needs mod/shift/alt, and the final key.
export const parseCombo = (combo) => {
  const parts = combo.split('+').map((part) => part.trim());
  const key = parts[parts.length - 1];
  return {
    key,
    needsMod: parts.includes('mod'),
    needsShift: parts.includes('shift'),
    needsAlt: parts.includes('alt')
  };
};

// Checks whether a KeyboardEvent matches a declarative combo string.
// Escape is exempt from the "no stray modifier" check below: it must close
// a modal even if the user is still holding Ctrl/Cmd from a preceding
// mod+S, e.g. a save-then-cancel chord without releasing the modifier key.
export const matchesCombo = (event, combo) => {
  const { key, needsMod, needsShift, needsAlt } = parseCombo(combo);
  const modPressed = isMac() ? event.metaKey : event.ctrlKey;

  if (needsMod && !modPressed) return false;
  if (combo !== 'Escape' && !needsMod && modPressed) return false;
  if (needsShift !== event.shiftKey) return false;
  if (needsAlt !== event.altKey) return false;

  return event.key.toLowerCase() === key.toLowerCase();
};

// Human-readable hint for tooltips, e.g. "⌘S" on Mac, "Ctrl+S" elsewhere.
export const formatCombo = (combo) => {
  const { key, needsMod, needsShift, needsAlt } = parseCombo(combo);
  const mac = isMac();
  const modLabel = mac ? '⌘' : 'Ctrl+';
  const shiftSymbol = mac ? '⇧' : 'Shift+';
  const altSymbol = mac ? '⌥' : 'Alt+';
  const shiftLabel = needsShift ? shiftSymbol : '';
  const altLabel = needsAlt ? altSymbol : '';
  const keyLabel = key.length === 1 ? key.toUpperCase() : key.replace('Arrow', '');

  return `${needsMod ? modLabel : ''}${shiftLabel}${altLabel}${keyLabel}`;
};
